import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setDeletePin } from '../services/api';
import { Button } from './ui/button';
import { LogOut, KeyRound } from 'lucide-react';
import SetPinDialog from './SetPinDialog';
import { SignOutEverywhereButton } from './SignOutEverywhereButton';
import { toast } from 'sonner';

const Navbar = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSetPin, setShowSetPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSetPin = async (pin, password) => {
    setPinLoading(true);
    try {
      await setDeletePin(pin, password);
      toast.success('Delete PIN set successfully');
      setShowSetPin(false);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const navItems = [
    { path: '/companies', label: 'Companies', roles: ['Super Admin'] },
    { path: '/dashboard', label: 'Dashboard', roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/vendors', label: 'Vendors', roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/banks', label: 'Banks', roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/phone-numbers', label: 'Phone Numbers', roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/partners', label: 'Branches', roles: ['Admin', 'Super Admin'] },
    { path: '/users', label: 'Users', roles: ['Admin', 'Sub Admin', 'Super Admin'] },
    { path: '/activity-logs', label: 'Logs', roles: ['Admin', 'Super Admin'] },
    { path: '/reports', label: 'Reports', roles: ['Admin', 'Sub Admin', 'Super Admin'] },
    { path: '/settings', label: 'Settings', roles: ['Admin', 'Super Admin'] },
    { path: '/security-timeline', label: 'Security', roles: ['Super Admin'] },
    { path: '/system-health', label: 'System Health', roles: ['Super Admin'] },
    { path: '/my-banks', label: 'My Banks', roles: ['Partner'] },
  ];

  return (
    <>
      <nav className="desktop-navbar sticky top-0 z-30 bg-slate-900 border-b border-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div title={`Bank Vendor Portal — ${user?.role} · ${user?.name}`} className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-green-500 flex items-center justify-center font-bold text-white shadow-lg text-sm">
                  BV
                </div>
                <div className="hidden">
                  <div className="font-bold text-sm leading-tight">Bank Vendor Portal</div>
                  <div className="text-[10px] text-slate-400 leading-tight">{user?.role} · {user?.name}</div>
                </div>
              </div>
              <div className="flex gap-1 ml-4 min-w-0 overflow-x-auto" data-testid="desktop-nav-items">
                {navItems.map((item) => {
                  if (!item.roles.includes(user?.role)) return null;
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className={`px-2 py-2 rounded-lg text-[13px] font-semibold whitespace-nowrap flex-shrink-0 transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {(user?.role === 'Admin' || user?.role === 'Super Admin') && (
                <Button
                  onClick={() => setShowSetPin(true)}
                  variant="outline"
                  size="sm"
                  data-testid="set-pin-btn"
                  className="border-slate-700 text-white hover:bg-slate-800 gap-1"
                  title={user?.hasDeletePin ? 'Change delete PIN' : 'Set delete PIN'}
                >
                  <KeyRound className="w-4 h-4" />
                </Button>
              )}
              <SignOutEverywhereButton className="border-slate-700 text-white hover:bg-slate-800" />
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-slate-700 text-white hover:bg-slate-800"
                title={`${user?.name} (${user?.role}) — Logout`}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <SetPinDialog
        open={showSetPin}
        onOpenChange={setShowSetPin}
        onSubmit={handleSetPin}
        loading={pinLoading}
        isUpdate={user?.hasDeletePin}
      />
    </>
  );
};

export default Navbar;
